import { type KeyboardEvent, type MouseEvent, useEffect, useMemo, useState } from 'react';

import { href, useLocation, useNavigate } from 'react-router';

import { ROUTES } from '@crm/shared/routes';

import type { BackNavigationState } from '@crm/shared/routes';

type DealTableItem = {
    id: number;
};

const INTERACTIVE_ELEMENT_SELECTOR =
    'button, a, input, textarea, select, [role="checkbox"], [role="option"], [data-row-navigation="ignore"]';

const isInteractiveElement = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest(INTERACTIVE_ELEMENT_SELECTOR));

type DealTableRowEvent =
    | MouseEvent<HTMLTableRowElement>
    | KeyboardEvent<HTMLTableRowElement>;

const isEventFromRow = (event: DealTableRowEvent) =>
    event.target instanceof Node && event.currentTarget.contains(event.target);

export const useDealsTable = ({
    dealItems,
}: {
    dealItems: readonly DealTableItem[];
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dealIds = useMemo(() => dealItems.map((deal) => deal.id), [dealItems]);
    const [selectedDealIds, setSelectedDealIds] = useState<Set<number>>(() => new Set());

    useEffect(() => {
        setSelectedDealIds(new Set());
    }, [dealIds]);

    const isAllDealsSelected =
        dealIds.length > 0 && dealIds.every((dealId) => selectedDealIds.has(dealId));
    const isSomeDealsSelected = dealIds.some((dealId) => selectedDealIds.has(dealId));

    const handleSelectAllDeals = (checked: boolean) => {
        setSelectedDealIds(checked ? new Set(dealIds) : new Set());
    };

    const handleSelectDeal = (dealId: number, checked: boolean) => {
        setSelectedDealIds((currentSelectedDealIds) => {
            const nextSelectedDealIds = new Set(currentSelectedDealIds);

            if (checked) {
                nextSelectedDealIds.add(dealId);
            } else {
                nextSelectedDealIds.delete(dealId);
            }

            return nextSelectedDealIds;
        });
    };

    const clearSelectedDeals = () => {
        setSelectedDealIds(new Set());
    };

    const navigateToDeal = (dealId: number) => {
        navigate(href(ROUTES.DEAL, { id: String(dealId) }), {
            state: { fromRoute: location.pathname } satisfies BackNavigationState,
        });
    };

    const handleRowClick = (event: MouseEvent<HTMLTableRowElement>, dealId: number) => {
        if (isEventFromRow(event) && !isInteractiveElement(event.target)) {
            navigateToDeal(dealId);
        }
    };

    const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, dealId: number) => {
        if (
            event.key === 'Enter' &&
            isEventFromRow(event) &&
            !isInteractiveElement(event.target)
        ) {
            navigateToDeal(dealId);
        }
    };

    return {
        clearSelectedDeals,
        handleRowClick,
        handleRowKeyDown,
        handleSelectAllDeals,
        handleSelectDeal,
        isAllDealsSelected,
        isSomeDealsSelected,
        selectedDealIds,
    };
};
